import React from 'react';
import { ArrowRight, Mail, MapPin, Quote, ShieldCheck } from 'lucide-react';
import { useSeo } from '../hooks/useSeo';

const ENGAGEMENTS = [
  {
    id: 'attentive-support',
    title: 'Un accompagnement attentif',
    text: 'Chaque projet commence par l’écoute de vos usages, de vos contraintes et de vos envies.',
  },
  {
    id: 'documented-design',
    title: 'Une conception documentée',
    text: 'Plans cotés, matières et rendus vous permettent de décider avec une vision claire à chaque étape.',
  },
  {
    id: 'trusted-follow-up',
    title: 'Un suivi de confiance',
    text: 'La coordination et les ajustements restent suivis jusqu’à la concrétisation de votre espace.',
  },
];

const REVIEWS = [
  {
    id: 'karen-noah-aubagne',
    author: 'Karen et Noah',
    project: 'Cuisine sur mesure · Aubagne',
    text: 'Tommy a pris le temps de comprendre notre espace et nos habitudes avant de proposer quoi que ce soit. Les plans cotés et la 3D nous ont permis de nous projeter précisément, et le résultat livré correspond exactement à ce qui avait été dessiné. Le suivi de chantier a été sérieux du début à la fin. Je recommande sans réserve.',
  },
  {
    id: 'claire-thomas-cassis',
    author: 'Claire et Thomas',
    project: 'Dressing sur mesure · Cassis · 21 juillet 2025',
    text: 'Le travail sur l’optimisation du moindre centimètre est impressionnant : nous avons gagné beaucoup plus de rangement que ce que nous imaginions possible dans cette pièce. Les finitions en noyer et l’éclairage intégré sont superbes. Prestation haut de gamme, à la hauteur du budget.',
  },
  {
    id: 'manu-cedric-salon-de-provence',
    author: 'Manu et Cédric',
    project: 'Rénovation de salle de bain · Salon-de-Provence · 2 février 2026',
    text: 'Douche à l’italienne, pierre naturelle : le rendu est exactement celui des images 3D présentées au départ. Bonne coordination avec les artisans, délais respectés. Un vrai travail de précision.',
  },
  {
    id: 'sebastien-penderie',
    author: 'Sébastien',
    project: 'Agencement de penderie · 4 avril 2026',
    text: 'Excellent accompagnement pour l’agencement de notre penderie. Conseils pertinents, plans clairs, réalisation soignée. Merci pour le suivi !',
  },
  {
    id: 'societe-anonyme-bureaux',
    author: 'Société anonyme',
    project: 'Agencement de bureaux · Client professionnel',
    text: 'Approche technique rigoureuse, plans cotés livrés dans les délais, et une vraie capacité à s’adapter aux contraintes du site. Collaboration que nous renouvellerons.',
  },
];

export default function ReviewsPage() {
  useSeo('Avis clients', "Découvrez l’approche de JENMA Concept pour des projets d’agencement intérieur sur mesure, depuis Salon-de-Provence.", '/avis-clients');

  return (
    <main className="min-h-screen bg-[#121212] text-gray-200" data-testid="reviews-page">
      <section className="container mx-auto px-6 pt-36 pb-24 lg:pt-48">
        <p className="mb-5 flex items-center gap-3 text-[11px] uppercase tracking-[0.4em] text-gold" data-testid="reviews-page-eyebrow"><span className="h-px w-10 bg-gold" />Avis clients</p>
        <div className="grid items-end gap-12 lg:grid-cols-[1.15fr_0.85fr]">
          <div>
            <h1 className="max-w-3xl font-playfair text-4xl leading-tight text-white sm:text-5xl lg:text-6xl">La confiance se construit <em className="text-[#d9c3a0]">dans le détail.</em></h1>
            <p className="mt-6 max-w-2xl text-sm leading-relaxed text-gray-400 sm:text-base" data-testid="reviews-page-intro">JENMA Concept accompagne chaque projet avec exigence, clarté et discrétion. Les retours clients sont publiés ici uniquement avec leur accord, afin de refléter fidèlement chaque collaboration.</p>
          </div>
          <aside className="border-l border-gold/40 pl-6" data-testid="reviews-page-location">
            <MapPin className="mb-4 h-5 w-5 text-neon" />
            <p className="text-xs uppercase tracking-[0.25em] text-gray-500">Basé à</p>
            <p className="mt-1 font-playfair text-2xl text-white">Salon-de-Provence</p>
            <p className="mt-2 text-sm text-gray-400">Provence-Alpes-Côte d’Azur, Paris et France entière sur demande.</p>
          </aside>
        </div>

        <section className="mt-20 border-y border-white/10 py-10" aria-labelledby="engagements-title" data-testid="reviews-page-commitments">
          <h2 id="engagements-title" className="font-playfair text-2xl text-white">Ce que nos clients peuvent attendre</h2>
          <div className="mt-8 grid gap-8 md:grid-cols-3">
            {ENGAGEMENTS.map((engagement) => (
              <article key={engagement.id} className="border-t border-gold/30 pt-5" data-testid={`reviews-commitment-${engagement.id}`}>
                <ShieldCheck className="mb-4 h-5 w-5 text-gold" />
                <h3 className="font-playfair text-xl text-white">{engagement.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-gray-400">{engagement.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="mt-16" aria-labelledby="testimonials-title" data-testid="reviews-page-testimonials">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-[11px] uppercase tracking-[0.32em] text-neon">Témoignages publiés avec accord</p>
              <h2 id="testimonials-title" className="mt-3 font-playfair text-2xl text-white">Des projets racontés par leurs propriétaires</h2>
            </div>
          </div>
          <div className="mt-8 grid gap-6">
            {REVIEWS.map((review) => (
              <article key={review.id} className="border border-gold/25 bg-[#171717] p-8 sm:p-10" data-testid={`client-review-${review.id}`}>
                <Quote className="h-7 w-7 text-gold" aria-hidden="true" />
                <blockquote className="mt-5 max-w-4xl font-playfair text-xl leading-relaxed text-white sm:text-2xl">« {review.text} »</blockquote>
                <footer className="mt-7 border-t border-white/10 pt-5">
                  <p className="font-semibold text-[#d9c3a0]">{review.author}</p>
                  <p className="mt-1 text-sm text-gray-400">{review.project}</p>
                </footer>
              </article>
            ))}
          </div>
        </section>

        <section className="mt-16 grid gap-8 border border-gold/25 bg-[#171717] p-8 lg:grid-cols-[1fr_auto] lg:items-center" data-testid="reviews-page-verification-note">
          <div>
            <p className="text-[11px] uppercase tracking-[0.32em] text-neon">Avis vérifiés</p>
            <h2 className="mt-3 font-playfair text-2xl text-white">Une bibliothèque de retours authentiques</h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-gray-400">Les témoignages détaillés sont ajoutés après autorisation de leurs auteurs. Cette page reste volontairement fidèle : aucun avis ni note n’est publié sans retour client réel et vérifiable.</p>
          </div>
          <a href="mailto:tommy@jenmaconcept.com?subject=Mon%20avis%20sur%20JENMA%20Concept" data-testid="reviews-share-feedback-link" className="group inline-flex items-center justify-center gap-2 bg-gold px-6 py-3 font-semibold text-dark transition-colors duration-300 hover:bg-gold-light">
            <Mail className="h-4 w-4" /> Partager mon retour <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </section>
      </section>
    </main>
  );
}